import React, { useEffect, useState } from 'react';
import { Row, Col, Card, ButtonGroup, Button, Table, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import constants from '../../const.js';
import { Kpi, BarChart } from './sceneHistoryDashboard.js';

const RANGES = [7, 14, 30, 90];

const AzureCostDashboard = () => {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        const refresh = refreshKey > 0 ? '&refresh=true' : '';
        axios.get(`${constants.DATA1_API_URL}/admin/azure-cost/foundry?days=${days}${refresh}`, {
            headers: { 'Authorization': token }
        }).then(res => { if (!cancelled) setData(res.data); })
            .catch(err => {
                if (!cancelled) setError(err.response?.data?.message || 'Failed to load Azure cost data');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [days, refreshKey]);

    const currency = data?.currency || 'USD';
    const money = (n) => new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
    const t = data?.totals || {};
    const chartData = (data?.daily || []).map(d => ({ date: d.date, cost: Math.round(d.cost * 100) / 100 }));

    return (
        <div>
            <Row className="align-items-center mb-3">
                <Col><h4 className="mb-0">Azure Foundry — Daily Cost</h4></Col>
                <Col xs="auto">
                    <ButtonGroup size="sm" className="mr-2">
                        {RANGES.map(r => (
                            <Button key={r} variant={days === r ? 'primary' : 'outline-primary'} onClick={() => setDays(r)}>{r}d</Button>
                        ))}
                    </ButtonGroup>
                    <Button size="sm" variant="outline-secondary" disabled={loading} onClick={() => setRefreshKey(k => k + 1)}>Refresh</Button>
                </Col>
            </Row>

            {loading ? <div className="text-center py-3"><Spinner animation="border" size="sm" /></div> : null}
            {error ? <Alert variant="danger">{error}</Alert> : null}
            {data?.stale ? <Alert variant="warning">Azure is unavailable right now; showing cached data.</Alert> : null}

            {data ? <>
                <Row className="mb-3">
                    <Col xs={6} md={3} className="mb-2"><Kpi label={`Total (${data.days}d)`} value={money(t.total)} /></Col>
                    <Col xs={6} md={3} className="mb-2"><Kpi label="Daily average" value={money(t.dailyAverage)} /></Col>
                    <Col xs={6} md={3} className="mb-2"><Kpi label="Latest day" value={money(t.today)} sub={data.to} /></Col>
                    <Col xs={6} md={3} className="mb-2"><Kpi label="Peak day" value={money(t.peakCost)} sub={t.peakDay || '-'} /></Col>
                </Row>

                <Card className="shadow-sm mb-3">
                    <Card.Header>Cost per day ({currency})</Card.Header>
                    <Card.Body>
                        <BarChart data={chartData} labelKey="date" valueKey="cost" height={220}
                            formatLabel={(d) => d} xLabel={chartData.length <= 14 ? (d) => d.slice(5) : undefined} />
                    </Card.Body>
                </Card>

                <Row>
                    <Col md={6} className="mb-3">
                        <Card className="h-100 shadow-sm">
                            <Card.Header>By service</Card.Header>
                            <Table size="sm" hover className="mb-0">
                                <tbody>
                                    {data.byService.map(s => (
                                        <tr key={s.service}><td>{s.service}</td><td className="text-right">{money(s.cost)}</td></tr>
                                    ))}
                                    {data.byService.length === 0 ? <tr><td className="text-muted">No cost data for this period</td></tr> : null}
                                </tbody>
                            </Table>
                        </Card>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Card className="h-100 shadow-sm">
                            <Card.Header>By resource</Card.Header>
                            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                                <Table size="sm" hover className="mb-0">
                                    <tbody>
                                        {data.byResource.map(r => (
                                            <tr key={`${r.service}|${r.resource}`}>
                                                <td className="text-truncate" style={{ maxWidth: 260 }} title={r.resource}>{r.resource}</td>
                                                <td className="text-right">{money(r.cost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card>
                    </Col>
                </Row>

                <Card className="shadow-sm">
                    <Card.Header>Daily breakdown</Card.Header>
                    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                        <Table size="sm" striped hover className="mb-0">
                            <thead><tr><th>Date</th><th className="text-right">Cost</th></tr></thead>
                            <tbody>
                                {[...data.daily].reverse().map(d => (
                                    <tr key={d.date}><td>{d.date}</td><td className="text-right">{money(d.cost)}</td></tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Card>
                <div className="text-muted small mt-2">
                    Azure cost data can lag by several hours. Cached up to 30 min · Updated {new Date(data.generatedAt).toLocaleString()}
                </div>
            </> : null}
        </div>
    );
};

export default AzureCostDashboard;
